import * as React from 'react'
import { Text, View } from 'react-native'
import styled from '@theme/styled-components'

import TextInput from '@components/textInput/TextInput'
import Choice from '@components/choice/Choice'
import Animators from '@components/root/Animators'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'

import * as SiteStore from '@store/site/site'
import * as AuthStore from '@store/auth/auth'

import { FormikHelpers, useFormik } from 'formik'

import sanitize from '@utils/Sanitizer'

import { AuthScope } from '@api/enum'

interface Values {
  manifestationId: string
}

const Manif = ({ form, onForm }: IncidentFormProps) => {
  const init: Values = React.useMemo(() => ({ manifestationId: form.manifestationId }), [form.manifestationId])

  const i18n = useI18n()
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations.inProgress)

  const [search, setSearch] = React.useState('')

  const manifs = React.useMemo(
    () =>
      manifestations
        .filter((m) => sanitize(m.libelle).includes(sanitize(search)))
        .sort((a, b) => a.libelle.localeCompare(b.libelle)),
    [manifestations, search]
  )

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    if (vals.manifestationId !== form.manifestationId) {
      onForm({ manifestationId: vals.manifestationId }).finally(() => setSubmitting(false))
    }

    onForm(vals).finally(() => setSubmitting(false))
  }

  const { values, setFieldValue, handleSubmit, dirty } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  const onSelect = (val: string) => {
    if (values.manifestationId !== val) {
      setFieldValue('manifestationId', val)
    }
  }

  const options = manifs.map((b) => ({ label: b.libelle, value: b.id }))
  const opt = options.find((o) => values.manifestationId === o.value)

  const noSelectOption = { label: i18n.t('screens.incident.MANIF.empty'), value: '' }

  return (
    <MainContainer>
      <Animators>
        <SearchContainer>
          <TextInput
            value={search}
            onChange={setSearch}
            placeholder={i18n.t('screens.incident.MANIF.search')}
            left={{ name: 'search' }}
          />
        </SearchContainer>

        {scopes.includes(AuthScope.DemandesHorsManifestationCreation) && (
          <>
            <Choice
              data={[noSelectOption]}
              onItemSelected={(next) => onSelect(next.value as string)}
              value={values.manifestationId ? undefined : noSelectOption}
            />
            <Info>{i18n.t('screens.incident.MANIF.warning')}</Info>
          </>
        )}

        <Title>{i18n.t('screens.incident.MANIF.manifs')}</Title>

        {options.length > 0 ? (
          <Choice data={options} onItemSelected={(next) => onSelect(next.value as string)} value={opt} />
        ) : (
          <MissingInfo>{i18n.t('screens.incident.MANIF.noManif')}</MissingInfo>
        )}
      </Animators>
    </MainContainer>
  )
}

export default Manif

const MainContainer = styled(View)`
  margin: 0 20px;
`
const SearchContainer = styled(View)`
  margin-bottom: 25px;
`
const Info = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  color: ${(props) => props.theme.colors.tertiary};
  margin-bottom: 20px;
`
const Title = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  margin-bottom: 10px;
  margin-top: 15px;
`
const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`
